import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, tap, switchMap } from 'rxjs/operators';

import type { ActivitiesFiltersParams } from '@core/activities-types';
import { createEffectFactory } from '@core/ngrx-legacy';

import { NotificationsHttpService } from './notifications-http.service';
import { NotificationsActions } from './notifications.store';
import { DefaultActivitiesAndNotificationsFilters } from '../../helpers/filters-helper';
import { resolveHasMore } from '../../helpers/has-more-helper';
import { ActivitiesDataScope } from '../../types';
import { NotificationsCounterActions } from '../notification-counter/notification-counter.store';

@Injectable({ providedIn: 'root' })
export class NotificationsEffects {
  private readonly notificationService = inject(NotificationsHttpService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  public effects = createEffectFactory(NotificationsActions, this.actions$);
  public initialFilters: ActivitiesFiltersParams = {
    ...DefaultActivitiesAndNotificationsFilters,
  };

  public refreshCounter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationsActions.markAllViewedSuccess, NotificationsActions.markAsViewedSuccess),
        tap(() => {
          this.store.dispatch(NotificationsCounterActions.fetch());
        }),
      ),
    { dispatch: false },
  );

  public markAsViewed$ = this.effects(NotificationsActions.markAsViewed, ({ payload }) =>
    this.notificationService.markAsViewed(payload).pipe(
      map(() => NotificationsActions.markAsViewedSuccess()),
      catchError(error => of(NotificationsActions.markAllViewedFailed({ payload: error }))),
    ),
  );
  public markAsNotViewed$ = this.effects(NotificationsActions.markAsNotViewed, ({ payload }) =>
    this.notificationService.markAsNotViewed(payload).pipe(
      map(() => NotificationsActions.markAsViewedSuccess()),
      catchError(error => of(NotificationsActions.markAllViewedFailed({ payload: error }))),
    ),
  );

  public markAllViewed$ = this.effects(NotificationsActions.markAllViewed, () =>
    this.notificationService.markAllViewed().pipe(
      map(() => NotificationsActions.markAllViewedSuccess()),
      catchError(error => of(NotificationsActions.markAllViewedFailed({ payload: error }))),
    ),
  );

  public markAllNotViewed$ = this.effects(NotificationsActions.markAllNotViewed, () =>
    this.notificationService.markAllNotViewed().pipe(
      map(() => NotificationsActions.markAllViewedSuccess()),
      catchError(error => of(NotificationsActions.markAllViewedFailed({ payload: error }))),
    ),
  );

  public markAllViewedByCandidate$ = this.effects(
    NotificationsActions.markAllViewedByCandidate,
    ({ payload }) =>
      this.notificationService.markAllViewedByCandidate(payload).pipe(
        map(() => NotificationsActions.markAllViewedByCandidateSuccess()),
        catchError(error =>
          of(NotificationsActions.markAllViewedByCandidateFailed({ payload: error })),
        ),
      ),
  );

  public markViewedSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        NotificationsActions.markAsViewedSuccess,
        NotificationsActions.markAllViewedSuccess,
        NotificationsActions.markAllViewedByCandidateSuccess,
      ),
      switchMap(() =>
        of(
          NotificationsActions.fetchCollection({
            payload: { params: { ...this.initialFilters }, scope: ActivitiesDataScope.All },
          }),
        ),
      ),
    ),
  );

  public fetchTrackingNotifications$ = this.effects(
    NotificationsActions.fetchCollection,
    ({ payload }) =>
      this.notificationService.fetchTrackingNotifications(payload.params, payload.scope).pipe(
        map(res => ({
          data: res,
          scope: payload.scope!,
          replace: payload.replace!,
          hasMore: resolveHasMore(res.meta, res.activities.length, payload.params?.limit),
        })),
      ),
  );
}
